import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Paper,
    Alert,
    Stack,
    Chip,
    Card,
    CardContent,
    CircularProgress,
    useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PersonIcon from '@mui/icons-material/Person';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { useAppSelector } from '../../hooks/redux.hooks';
import axiosInstance from '../../services/axiosInstance';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import AccountSelector from '../common/AccountSelector';
import CostCenterSelector from '../common/CostCenterSelector';
import {
    CreateVoucherDto,
    CreateVoucherDetailDto,
    VoucherDto,
    UpdateVoucherDto,
} from '../../types/vouchers.types';
import { VOUCHER_SIDE } from '../../constants/vouchers.constants';
import { cashPaymentsService } from '../../services/cashPayments.service';
import apiClient from '../../services/api';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';

interface CashPaymentDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (dto: CreateVoucherDto | UpdateVoucherDto) => Promise<void>;
    editVoucher?: VoucherDto | null;
    cashParentAccountId: number;
}

interface DebitLine {
    id: string;
    accountId: number | null;
    costCenterId: number | null;
    amount: number;
    description: string;
}

const CashPaymentDialog: React.FC<CashPaymentDialogProps> = ({
    open,
    onClose,
    onSave,
    editVoucher,
    cashParentAccountId,
}) => {
    const { language } = useLanguage();
    const isRTL = language === 'ar';
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { minDate, maxDate } = useFiscalDateRange();

    const gridRef = useRef<HTMLDivElement>(null);
    useGridEnterNavigation(gridRef);

    const [voucherNumber, setVoucherNumber] = useState<string>('');
    const [voucherDate, setVoucherDate] = useState<string>(
        new Date().toISOString().split('T')[0]
    );
    const [notes, setNotes] = useState<string>('');
    const [payeeName, setPayeeName] = useState<string>('');
    const [creditAccountId, setCreditAccountId] = useState<number | null>(null);
    const [creditAmount, setCreditAmount] = useState<number>(0);
    const [debitLines, setDebitLines] = useState<DebitLine[]>([
        {
            id: Math.random().toString(),
            accountId: null,
            costCenterId: null,
            amount: 0,
            description: '',
        },
    ]);
    const [loading, setLoading] = useState(false);
    const [checkingNumber, setCheckingNumber] = useState(false);
    const [error, setError] = useState<string>('');

    // ── Smart Entry (Super Admin only) ────────────────────────
    const isSuperAdmin = useAppSelector(s => s.auth.user?.isSuperAdmin ?? false);
    const [smartOpen, setSmartOpen] = useState(false);
    const [smartText, setSmartText] = useState('');
    const [smartLoading, setSmartLoading] = useState(false);

    // ── Voice Smart Entry (Web Speech API — Chrome/Edge built-in) ──
    const [voiceListening, setVoiceListening] = useState(false);
    const [voiceInterim, setVoiceInterim] = useState('');
    const [voiceLang, setVoiceLang] = useState<'ar-SA' | 'en-US'>('ar-SA');
    const [audioLevel, setAudioLevel] = useState(0); // 0-100, real-time mic input level
    const voiceRecognitionRef = useRef<any>(null);
    const voiceShouldRestartRef = useRef(false);
    const voiceLangRef = useRef<'ar-SA' | 'en-US'>('ar-SA');
    const voiceRawRef = useRef('');  // raw accumulated voice transcript (pre-transform)
    const audioCtxRef = useRef<AudioContext | null>(null);
    const audioStreamRef = useRef<MediaStream | null>(null);
    const audioAnalyserRef = useRef<AnalyserNode | null>(null);
    const audioRafRef = useRef<number | null>(null);

    // ═════════════════════════════════════════════════════════════
    // Voice Transcript → Smart Entry Template Transformer
    // Converts spoken commands into the structured template format.
    // Trigger vocabulary is intentionally WIDE for tolerance.
    // ═════════════════════════════════════════════════════════════
    const transformVoiceText = (raw: string): string => {
        if (!raw) return '';
        let text = raw;

        // ── Step 1: Hindi/Persian/Indic digits → ASCII ────────
        text = text.replace(/[٠-٩]/g, (c: string) =>
            String.fromCharCode(c.charCodeAt(0) - 0x0660 + 0x30));
        text = text.replace(/[۰-۹]/g, (c: string) =>
            String.fromCharCode(c.charCodeAt(0) - 0x06F0 + 0x30));

        // ── Step 1.5: Strip Arabic diacritics (tashkeel) ────────
        // Chrome STT sometimes adds fatha/damma/kasra marks. Triggers must
        // match the bare letters, so strip these before further processing.
        text = text.replace(/[ً-ْٰٓ-ٕ]/g, '');

        // ── Step 1.7: Normalize numeric punctuation ─────────────
        // Chrome may insert a literal "." or "," between digits, possibly
        // with surrounding spaces, OR split decimal digits ("4 4 5" → "445").
        // Collapse them all into a unified "X.YYY" form.
        text = text.replace(/(\d+)\s*[.,،]\s*((?:\d\s*){1,5})(?=\s|$|[^\d\s.,])/g,
            (_m: string, a: string, b: string) => a + '.' + b.replace(/\s+/g, ''));

        // ── Step 2: Normalize whitespace ───────────────────────
        text = text.replace(/[ \t ]+/g, ' ').trim();

        // ── Step 3: NEW LINE triggers → \n-\n ─────────────────
        // Expanded vocabulary (Arabic, English, transliterations, common variants)
        const newLineTriggers = [
            'سطر\\s*جديد', 'اسطر\\s*جديد', 'السطر\\s*التالي', 'سطر\\s*تالي',
            'خط\\s*جديد', 'شرطة\\s*جديدة', 'فاصل', 'فاصل\\s*جديد',
            'نيو\\s*لاين', 'نيولاين',
            'تالي', 'التالي', 'تال', 'نكست',
            'new\\s*line', 'newline',
            'next', 'next\\s*line',
            'enter', 'انتر',
            'break', 'بريك',
            'separator', 'سيباريتر',
            'تفصيلة\\s*جديدة', 'سطر\\s*ثاني', 'سطر\\s*أخر', 'سطر\\s*اخر',
        ];
        const newLineRx = new RegExp('\\s*(?:' + newLineTriggers.join('|') + ')\\s*[،.,]?\\s*', 'giu');
        text = text.replace(newLineRx, '\n-\n');

        // ── Step 4: BENEFICIARY prefix normalization ───────────
        const payeeTriggers = [
            'المستفيد', 'مستفيد', 'إلى', 'الى\\s+(?=\\D)', // "الى X" but not "الى 5"
            'بنفيشاري', 'بينافيشري', 'بنيفيشري', 'بنيفيشاري',
            'beneficiary', 'paid\\s*to', 'paidto', 'payable\\s*to', 'payee',
            'pay\\s*to', 'pay',
            'المدفوع\\s*له', 'المدفوع\\s*ل', 'لصالح',
            'لمن', 'لـ', 'باسم',
        ];
        const payeeRx = new RegExp(
            '(^|\\n)\\s*(?:' + payeeTriggers.join('|') + ')\\s*[:.،]?\\s*',
            'giu');
        text = text.replace(payeeRx, '$1المستفيد ');

        // ── Step 5: Decimal point ("نقطة" / "dot") between digits ──
        // Capture ALL following digits (possibly separated by spaces) into the
        // decimal portion. E.g. "493 dot 4 4 5" → "493.445"
        const dotTriggers = ['نقطه', 'نقطة', 'فاصله', 'فاصلة', 'فصلة',
            'دوت', 'بوينت', 'dot', 'point', 'period', 'decimal'];
        const dotRx = new RegExp(
            '(\\d+)\\s*(?:' + dotTriggers.join('|') + ')\\s*((?:\\d\\s*){1,6})',
            'giu'
        );
        text = text.replace(dotRx, (_m: string, intPart: string, decPart: string) =>
            intPart + '.' + decPart.replace(/\s+/g, ''));

        // ── Step 6: KD-Fils combined patterns (Kuwait amount format) ──
        // 6a: "X دينار [dot|.|و] Y فلس" → "X.YYYkd" (full Kuwait amount form)
        text = text.replace(
            /(\d+)\s*(?:دينار(?:\s*كويتي)?|دنانير|دك|د\.\s*ك|kd|kwd|kuwaiti\s*dinar|كي\s*دي|كويتي)\s*(?:\.|نقطه|نقطة|دوت|dot|point|و|and|\+)?\s*(\d{1,3})\s*(?:فلس|فلوس|fils|فلسا|فلساً)\b/gi,
            (_m: string, kd: string, fils: string) => kd + '.' + fils.padStart(3, '0') + 'kd'
        );
        // 6b: "X.Y فلس" → "X.YYYkd"
        text = text.replace(
            /(\d+)\.(\d{1,3})\s*(?:فلس|فلوس|fils|فلسا|فلساً)\b/gi,
            (_m: string, kd: string, fils: string) => kd + '.' + fils.padStart(3, '0') + 'kd'
        );
        // 6c: "X و Y فلس" → "X.YYYkd"
        text = text.replace(
            /(\d+)\s+(?:و|and|\+)\s+(\d{1,3})\s*(?:فلس|فلوس|fils|فلسا|فلساً)\b/gi,
            (_m: string, kd: string, fils: string) => kd + '.' + fils.padStart(3, '0') + 'kd'
        );

        // ── Step 6.5: AMOUNT trigger → "Xkd" ────────────────────
        // Triggers: المبلغ / amount / etc. After the trigger, capture the number
        // (with optional decimal) and any trailing kd-suffix variant. Output: "Xkd".
        const amountTriggers = [
            'المبلغ', 'مبلغ', 'بمبلغ', 'بالمبلغ',
            'بقيمة', 'قيمة', 'بسعر', 'سعر', 'ثمن',
            'amount', 'the\\s*amount', 'amounts',
            'value', 'price', 'cost', 'total',
        ];
        // Comprehensive KD suffix variants (Arabic + English + transliterations)
        const kdSuffixes = [
            'دينار(?:\\s*كويتي)?', 'دنانير',
            'دك', 'د\\s*\\.?\\s*ك', 'د\\.?\\s*ك\\.?',
            'كي\\s*دي', 'ك\\s*دي', 'كي\\s*\\.?\\s*دي', 'ك\\s*\\.?\\s*د',
            'دي\\s*كي', 'دي\\s*ك', 'كويتي',
            'kd', 'kwd', 'k\\s*\\.?\\s*d', 'k\\.?d',
            'kuwaiti\\s*dinar',
        ];
        text = text.replace(
            new RegExp(
                '(?<=^|[\\s،,.])(?:' + amountTriggers.join('|') +
                ')[\\s:،,.]+(\\d+(?:\\.\\d+)?)\\s*(?:' + kdSuffixes.join('|') + ')?(?:(?=\\s)|(?=$)|(?=[،,.]))',
                'giu'
            ),
            (_m: string, num: string) => num + 'kd'
        );

        // ── Step 7: VOUCHER trigger + collect digits ──────────
        // Use lookbehind (?<=^|[\s،,.]) instead of \b because JavaScript \b
        // doesn't recognize Arabic word boundaries.
        const voucherTriggers = [
            'في', 'voucher', 'voucher\\s*number', 'voucher\\s*no', 'voucher\\s*#',
            'الفاوتشر', 'الفوتشر', 'فاوتشر', 'فوتشر',
            'باوتشر', 'بافتشر', 'بوتشر', 'باؤتشر', 'باؤوتشر', 'باؤچر',
            'ڤاوتشر', 'ڤوتشر', 'ڤافتشر',
            'سند\\s*رقم', 'رقم\\s*السند', 'رقم\\s*الفاوتشر', 'رقم\\s*الفوتشر', 'رقم\\s*الباوتشر',
            'نمبر\\s*السند', 'السند\\s*نمبر', 'نمبر\\s*فاوتشر', 'نمبر\\s*باوتشر',
            'v\\.?', 'vee', 'في\\s*رقم', 'الفي',
        ];
        const voucherRx = new RegExp(
            '(?<=^|[\\s،,.])(?:' + voucherTriggers.join('|') + ')\\s+([\\d\\s]+?)(?=$|\\n|[^\\d\\s])',
            'giu');
        text = text.replace(voucherRx, (_m: string, digits: string) =>
            'V ' + digits.replace(/\s+/g, ''));

        // ── Step 8: DATE trigger + collect numbers (→ dashes) ─
        // First: handle "today" → fill date with today
        const todayPattern = new RegExp(
            '(?<=^|[\\s،,.])(?:(?:التاريخ|تاريخ|date|بتاريخ)\\s+)?(?:اليوم|today|todays\\s*date|today\'?s)(?=$|[\\s،,.])',
            'giu');
        text = text.replace(todayPattern, () => {
            const t = new Date();
            return 'التاريخ ' + t.getDate() + '-' + (t.getMonth() + 1) + '-' + t.getFullYear();
        });

        const dateTriggers = [
            'التاريخ', 'تاريخ', 'بتاريخ', 'بتارخ', 'التارخ', 'تأريخ', 'date',
            'ديت', 'the\\s*date',
        ];
        const dateRx = new RegExp(
            '(?<=^|[\\s،,.])(?:' + dateTriggers.join('|') + ')\\s+([\\d\\s\\/\\-\\.\\\\]+?)(?=$|\\n|[^\\d\\s\\/\\-\\.\\\\])',
            'giu');
        text = text.replace(dateRx, (_m: string, nums: string) => {
            const parts = nums.trim().split(/[\s\/\-\.\\]+/).filter((p: string) => p.length > 0);
            return parts.length >= 1 ? 'التاريخ ' + parts.join('-') : _m;
        });

        // ── Step 9: Generic KD suffix without trigger → "Xkd" ───
        // Catches "475 دينار" or "493.445 kd" without the المبلغ trigger.
        text = text.replace(
            /(\d+(?:\.\d+)?)\s*(?:دينار(?:\s*كويتي)?|دنانير|دك|د\s*\.?\s*ك|كي\s*دي|ك\s*دي|كي\s*\.?\s*دي|دي\s*كي|كويتي|kuwaiti\s*dinar|kd|kwd|k\s*\.?\s*d)(?=$|[\s،,.]|[^a-zA-Z؀-ۿ])/gi,
            (_m: string, num: string) => num + 'kd');

        // ── Step 10: Standalone numbers on a line → append "kd" ──
        text = text.split('\n').map((line: string) => {
            const t = line.trim();
            // Skip V/Date lines
            if (/^\s*V\b/i.test(t)) return line;
            if (/^\s*(?:التاريخ|date)\b/i.test(t)) return line;
            // Skip the dash separator
            if (t === '-') return line;
            // Pure number line (with optional decimal) → append kd
            if (/^\d+(?:\.\d+)?$/.test(t)) return t + 'kd';
            return line;
        }).join('\n');

        // ── Step 11: Cleanup ────────────────────────────────────
        // Collapse spaces
        text = text.replace(/[ \t]+/g, ' ');
        // Strip spaces around newlines
        text = text.replace(/ *\n */g, '\n');
        // Collapse 3+ newlines to 2 (keeps -\n-\n separators tight)
        text = text.replace(/\n{3,}/g, '\n\n');
        // Remove stray "التاريخ التاريخ" (defensive)
        text = text.replace(/التاريخ\s+التاريخ/g, 'التاريخ');
        // Remove stray "المستفيد المستفيد"
        text = text.replace(/المستفيد\s+المستفيد/g, 'المستفيد');
        // Remove duplicate "V V"
        text = text.replace(/\bV\s+V\b/g, 'V');
        // Defensive: avoid double "kdkd"
        text = text.replace(/kd\s*kd\b/gi, 'kd');
        // Defensive: trim "kd" appearing alone on a line (no number) — orphan from cleanup
        text = text.split('\n').map(l => /^\s*kd\s*$/i.test(l) ? '' : l).filter(l => l !== '').join('\n');
        // Final newline pass
        text = text.replace(/\n{3,}/g, '\n\n');

        return text.trim();
    };

    // Start a separate audio analyser so the user SEES if the mic is being picked up.
    // Independent from SpeechRecognition (which doesn't expose level data).
    const startAudioLevelMeter = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            audioStreamRef.current = stream;
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            audioCtxRef.current = ctx;
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 512;
            source.connect(analyser);
            audioAnalyserRef.current = analyser;
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            const tick = () => {
                analyser.getByteTimeDomainData(dataArray);
                // RMS-style level computation
                let sum = 0;
                for (let i = 0; i < dataArray.length; i++) {
                    const v = (dataArray[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / dataArray.length);
                setAudioLevel(Math.min(100, Math.round(rms * 300)));
                audioRafRef.current = requestAnimationFrame(tick);
            };
            tick();
        } catch (err) {
            // Permission denied or no mic — show clear error
            toast.error(isRTL
                ? 'لم نتمكن من الوصول للميكروفون. تحقق من إعدادات Windows والمتصفح.'
                : 'Cannot access microphone. Check Windows and browser settings.');
        }
    };

    const stopAudioLevelMeter = () => {
        if (audioRafRef.current) cancelAnimationFrame(audioRafRef.current);
        audioRafRef.current = null;
        try { audioStreamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
        try { audioCtxRef.current?.close(); } catch {}
        audioStreamRef.current = null;
        audioCtxRef.current = null;
        audioAnalyserRef.current = null;
        setAudioLevel(0);
    };

    const isSpeechSupported = (): boolean => {
        return !!(window as any).webkitSpeechRecognition || !!(window as any).SpeechRecognition;
    };

    // Build a fresh recognition instance and start it.
    // Called both on initial Voice Input click AND on every silent auto-restart.
    const buildAndStartRecognition = () => {
        const SR = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
        if (!SR) return;
        try {
            const rec = new SR();
            rec.continuous = true;
            rec.interimResults = true;
            rec.lang = voiceLangRef.current;
            rec.maxAlternatives = 1;

            rec.onresult = (event: any) => {
                let interim = '';
                let final = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        final += transcript + ' ';
                    } else {
                        interim += transcript;
                    }
                }
                setVoiceInterim(interim);
                if (final.trim()) {
                    // Accumulate raw transcript and re-transform the full text every time.
                    // Re-transforming the full buffer (instead of appending transformed chunks)
                    // preserves cross-chunk context — e.g. "في" arrives in one chunk,
                    // "6 3 2 7" in the next; transform of the joined raw still produces "V 6327".
                    voiceRawRef.current = (voiceRawRef.current + ' ' + final.trim()).trim();
                    setSmartText(transformVoiceText(voiceRawRef.current));
                }
            };

            rec.onerror = (e: any) => {
                const err = e?.error || '';
                // "no-speech" and "aborted" are normal during silent periods — just restart silently.
                // "audio-capture" means no mic detected — also try to restart.
                if (err === 'no-speech' || err === 'aborted' || err === 'audio-capture') {
                    return;  // will be re-attempted in onend if user hasn't stopped
                }
                if (err === 'not-allowed' || err === 'service-not-allowed') {
                    toast.error(isRTL
                        ? 'يجب السماح للموقع باستخدام الميكروفون. افتح إعدادات المتصفح وامنح الإذن.'
                        : 'Microphone permission denied. Please enable mic access in browser settings.');
                    voiceShouldRestartRef.current = false;
                    setVoiceListening(false);
                    setVoiceInterim('');
                    return;
                }
                // Unknown errors — show but keep listening
                toast.warning((isRTL ? 'تحذير: ' : 'Warning: ') + err);
            };

            rec.onend = () => {
                // Browser auto-stops on long silence — restart unless user pressed Stop.
                if (voiceShouldRestartRef.current) {
                    setTimeout(() => {
                        if (voiceShouldRestartRef.current) buildAndStartRecognition();
                    }, 200);
                } else {
                    setVoiceListening(false);
                    setVoiceInterim('');
                }
            };

            voiceRecognitionRef.current = rec;
            rec.start();
        } catch (err: any) {
            // start() can throw "InvalidStateError" if already started — ignore and retry
            setTimeout(() => {
                if (voiceShouldRestartRef.current) buildAndStartRecognition();
            }, 300);
        }
    };

    const startVoiceCapture = async () => {
        if (!isSpeechSupported()) {
            toast.error(isRTL
                ? 'متصفحك لا يدعم الإدخال الصوتي. استخدم Chrome أو Edge.'
                : 'Your browser does not support voice input. Use Chrome or Edge.');
            return;
        }
        voiceLangRef.current = voiceLang;
        voiceShouldRestartRef.current = true;
        // Initialize raw buffer from current text (preserves prior content, allows resume)
        if (!voiceRawRef.current) voiceRawRef.current = smartText;
        setVoiceListening(true);
        await startAudioLevelMeter();
        buildAndStartRecognition();
        toast.info(isRTL
            ? `استمع (${voiceLang === 'ar-SA' ? 'عربي' : 'إنجليزي'})... قل "سطر جديد" للفصل بين الحقول`
            : `Listening (${voiceLang === 'ar-SA' ? 'AR' : 'EN'})... say "new line" between fields`);
    };

    const stopVoiceCapture = () => {
        voiceShouldRestartRef.current = false;
        try { voiceRecognitionRef.current?.stop(); } catch {}
        stopAudioLevelMeter();
        setVoiceListening(false);
        setVoiceInterim('');
    };

    // Cleanup recognition when dialog closes
    useEffect(() => {
        if (!smartOpen && voiceListening) {
            stopVoiceCapture();
        }
    }, [smartOpen]);

    const applySmartParse = async () => {
        if (!smartText.trim()) {
            toast.warning(isRTL ? 'اكتب جملة الإدخال' : 'Enter input text');
            return;
        }
        setSmartLoading(true);
        try {
            const { data } = await axiosInstance.post('/SmartEntry/parse', { text: smartText });
            // Date — use the literal yyyy-MM-dd part from backend.
            // DO NOT pass through new Date() — that converts to UTC and may shift the day
            // backward in negative timezones (e.g. "2026-02-01" → "2026-01-31").
            if (data.date) {
                const datePart = String(data.date).substring(0, 10);
                if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                    setVoucherDate(datePart);
                }
            }
            // Payee
            if (data.payeeEmployee) {
                setPayeeName(data.outputLanguage === 'ar'
                    ? (data.payeeEmployee.fullNameAr || data.payeeEmployee.fullName)
                    : (data.payeeEmployee.fullName || data.payeeEmployee.fullNameAr));
            } else if (data.payeeName) {
                setPayeeName(data.payeeName);
            }
            // Credit account (auto: Safe Box Main Cash)
            if (data.creditAccount?.accountId) {
                setCreditAccountId(data.creditAccount.accountId);
            }
            // Debit lines — accept lines even without amount (user can type it manually)
            // as long as a debit account was matched. Both ZERO-amount AND non-zero are kept.
            if (Array.isArray(data.lines) && data.lines.length > 0) {
                const newLines: DebitLine[] = data.lines
                    .filter((l: any) => l.debitAccount?.accountId)
                    .map((l: any) => ({
                        id: Math.random().toString(),
                        accountId: l.debitAccount.accountId,
                        costCenterId: l.costCenter?.costCenterId ?? null,
                        amount: Number(l.amount) || 0,
                        description: l.description || '',
                    }));
                if (newLines.length > 0) {
                    setDebitLines(newLines);
                    // Round to 3 decimals (KWD) to avoid floating-point artifacts
                    const total = newLines.reduce((s, l) => s + l.amount, 0);
                    setCreditAmount(Math.round(total * 1000) / 1000);
                }
            }
            // Notes from parser (V XXXX reference, date-range hint, etc.)
            if (Array.isArray(data.notes) && data.notes.length > 0) {
                setNotes(data.notes.join(' · '));
            }
            toast.success(isRTL
                ? `تم ملء السند — ${data.lines?.length || 0} سطر. راجع قبل الحفظ.`
                : `Voucher filled — ${data.lines?.length || 0} line(s). Review before saving.`);
            setSmartOpen(false);
            setSmartText('');
        } catch (e: any) {
            const status = e?.response?.status;
            const msg = status === 403
                ? (isRTL ? 'مرفوض — للسوبر أدمن فقط' : 'Forbidden — Super Admin only')
                : (e?.response?.data?.message || (isRTL ? 'فشل التحليل' : 'Parse failed'));
            toast.error(msg);
        } finally {
            setSmartLoading(false);
        }
    };

    const generateAutoNumber = async () => {
        try {
            const year = new Date().getFullYear();
            const vouchers = await cashPaymentsService.getAll();
            const currentYearVouchers = vouchers.filter((v) =>
                v.voucherNumber.startsWith(`CP-${year}-`)
            );
            let maxNum = 0;
            currentYearVouchers.forEach((v) => {
                const parts = v.voucherNumber.split('-');
                if (parts.length === 3) {
                    const num = parseInt(parts[2]);
                    if (!isNaN(num) && num > maxNum) maxNum = num;
                }
            });
            const nextNum = (maxNum + 1).toString().padStart(4, '0');
            return `CP-${year}-${nextNum}`;
        } catch (err) {
            return `CP-${new Date().getFullYear()}-0001`;
        }
    };

    const checkVoucherNumberExists = async (
        number: string
    ): Promise<boolean> => {
        try {
            const vouchers = await cashPaymentsService.getAll();
            return vouchers.some(
                (v) =>
                    v.voucherNumber === number &&
                    v.voucherId !== editVoucher?.voucherId
            );
        } catch (err) {
            return false;
        }
    };

    const resetForm = () => {
        setVoucherNumber('');
        setVoucherDate(new Date().toISOString().split('T')[0]);
        setNotes('');
        setPayeeName('');
        setCreditAccountId(null);
        setCreditAmount(0);
        setDebitLines([
            {
                id: Math.random().toString(),
                accountId: null,
                costCenterId: null,
                amount: 0,
                description: '',
            },
        ]);
        setError('');
    };

    const loadEditData = (voucher: VoucherDto) => {
        setVoucherNumber(voucher.voucherNumber);
        setVoucherDate(voucher.voucherDate.split('T')[0]);
        setNotes(voucher.notes || '');
        setPayeeName(voucher.payeeName || '');
        if (!voucher.details || voucher.details.length === 0) {
            setError(
                isRTL
                    ? 'لا توجد تفاصيل في السند'
                    : 'No details found in voucher'
            );
            return;
        }
        const creditDetail = voucher.details.find(
            (d) => d.side?.toLowerCase() === VOUCHER_SIDE.CREDIT.toLowerCase()
        );
        if (creditDetail) {
            setCreditAccountId(creditDetail.accountId || null);
            setCreditAmount(creditDetail.allocatedAmount);
        }
        const debitDetails = voucher.details.filter(
            (d) => d.side?.toLowerCase() === VOUCHER_SIDE.DEBIT.toLowerCase()
        );
        if (debitDetails.length > 0) {
            setDebitLines(
                debitDetails.map((d) => ({
                    id: Math.random().toString(),
                    accountId: d.accountId || null,
                    costCenterId: d.costCenterId || null,
                    amount: d.allocatedAmount,
                    description: d.description || '',
                }))
            );
        }
    };

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (open) {
            if (editVoucher) {
                timer = setTimeout(() => loadEditData(editVoucher), 100);
            } else {
                resetForm();
                generateAutoNumber().then(setVoucherNumber);
            }
        }
        return () => clearTimeout(timer);
    }, [open, editVoucher?.voucherId]);

    const addDebitLine = () =>
        setDebitLines([
            ...debitLines,
            {
                id: Math.random().toString(),
                accountId: null,
                costCenterId: null,
                amount: 0,
                description: '',
            },
        ]);
    const removeDebitLine = (id: string) => {
        if (debitLines.length > 1)
            setDebitLines(debitLines.filter((line) => line.id !== id));
    };
    const updateDebitLine = (id: string, field: keyof DebitLine, value: any) =>
        setDebitLines(
            debitLines.map((line) =>
                line.id === id ? { ...line, [field]: value } : line
            )
        );
    const isAccountUsed = (
        accountId: number | null,
        currentLineId?: string
    ): boolean => {
        if (!accountId) return false;
        if (accountId === creditAccountId) return true;
        return debitLines.some(
            (line) => line.id !== currentLineId && line.accountId === accountId
        );
    };

    const totalDebit = Math.round(
        debitLines.reduce((sum, line) => sum + (line.amount || 0), 0) * 1000
    ) / 1000;
    const totalCredit = creditAmount || 0;
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
    const difference = totalDebit - totalCredit;

    useEffect(() => {
        setCreditAmount(totalDebit);
    }, [totalDebit]);

    const validate = async (): Promise<boolean> => {
        if (!voucherNumber.trim()) {
            setError(isRTL ? 'رقم السند مطلوب' : 'Voucher number is required');
            return false;
        }
        setCheckingNumber(true);
        const exists = await checkVoucherNumberExists(voucherNumber);
        setCheckingNumber(false);
        if (exists) {
            setError(
                isRTL
                    ? 'رقم السند مستخدم مسبقاً'
                    : 'Voucher number already exists'
            );
            return false;
        }
        if (!voucherDate) {
            setError(isRTL ? 'التاريخ مطلوب' : 'Date is required');
            return false;
        }
        if (!payeeName.trim()) {
            setError(isRTL ? 'اسم المستفيد مطلوب' : 'Payee name is required');
            return false;
        }
        if (!creditAccountId) {
            setError(isRTL ? 'الصندوق مطلوب' : 'Cash account is required');
            return false;
        }
        if (totalCredit <= 0) {
            setError(
                isRTL
                    ? 'المبلغ يجب أن يكون أكبر من صفر'
                    : 'Amount must be greater than zero'
            );
            return false;
        }
        if (!isBalanced) {
            setError(
                isRTL ? 'المدين يجب أن يساوي الدائن' : 'Debit must equal Credit'
            );
            return false;
        }
        const invalidDebitLines = debitLines.filter(
            (line) => !line.accountId || line.amount <= 0
        );
        if (invalidDebitLines.length > 0) {
            setError(
                isRTL
                    ? 'جميع الأسطر يجب أن تحتوي على حساب ومبلغ'
                    : 'All lines must have account and amount'
            );
            return false;
        }
        for (const line of debitLines) {
            if (line.accountId) {
                try {
                    const accountResponse = await apiClient.get(
                        '/ChartOfAccounts/' + line.accountId
                    );
                    const account = accountResponse.data.data;
                    if (
                        account &&
                        account.requireCostCenter &&
                        !line.costCenterId
                    ) {
                        const msg =
                            (isRTL ? 'الحساب' : 'Account') +
                            ' "' +
                            (isRTL
                                ? account.accountNameAr
                                : account.accountName) +
                            '" ' +
                            (isRTL
                                ? 'يتطلب مركز تكلفة'
                                : 'requires Cost Center');
                        setError(msg);
                        toast.warning(msg);
                        return false;
                    }
                } catch (err) {
                    console.error('Error validating account:', err);
                }
            }
        }

        setError('');
        return true;
    };

    const handleSave = async () => {
        if (!(await validate())) return;
        try {
            setLoading(true);
            setError('');
            const details: CreateVoucherDetailDto[] = [];
            details.push({
                allocatedAmount: creditAmount,
                accountId: creditAccountId,
                side: VOUCHER_SIDE.CREDIT,
                costCenterId: null,
                description: notes,
            });
            debitLines.forEach((line) =>
                details.push({
                    allocatedAmount: line.amount,
                    accountId: line.accountId,
                    side: VOUCHER_SIDE.DEBIT,
                    costCenterId: line.costCenterId,
                    description: line.description,
                })
            );
            if (editVoucher) {
                await onSave({
                    voucherId: editVoucher.voucherId,
                    voucherNumber,
                    voucherDate,
                    voucherType: 'CP',
                    amount: totalCredit,
                    currencyCode: 'KWD',
                    exchangeRate: 1,
                    notes,
                    payeeName,
                    details,
                });
            } else {
                await onSave({
                    voucherNumber,
                    voucherDate,
                    voucherType: 'CP',
                    amount: totalCredit,
                    currencyCode: 'KWD',
                    exchangeRate: 1,
                    notes,
                    payeeName,
                    details,
                });
            }
            toast.success(isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully');
            handleClose();
        } catch (err: any) {
            const errorMsg =
                err.message || (isRTL ? 'حدث خطأ' : 'An error occurred');
            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            dir={isRTL ? 'rtl' : 'ltr'}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: isDarkMode
                        ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
                        : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    height: '95vh',
                    border: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                },
            }}
        >
            <DialogTitle
                sx={{
                    background: isDarkMode
                        ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2,
                    borderBottom: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <MonetizationOnIcon sx={{ fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="bold">
                        {editVoucher
                            ? isRTL
                                ? 'تعديل سند صرف نقدي'
                                : 'Edit Cash Payment'
                            : isRTL
                              ? 'سند صرف نقدي جديد'
                              : 'New Cash Payment'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!editVoucher && isSuperAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<AutoFixHighIcon />}
                            onClick={() => setSmartOpen(true)}
                            sx={{
                                background: 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)',
                                color: 'white',
                                fontWeight: 'bold',
                                '&:hover': { background: 'linear-gradient(135deg, #6a3de8 0%, #9c70f5 100%)' },
                            }}
                        >
                            {isRTL ? '🪄 إدخال ذكي' : '🪄 Smart Entry'}
                        </Button>
                    )}
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Smart Entry sub-dialog */}
            <Dialog open={smartOpen} onClose={() => setSmartOpen(false)} maxWidth="md" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
                <DialogTitle sx={{ background: 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)', color: 'white' }}>
                    🪄 {isRTL ? 'الإدخال الذكي بالنص الطبيعي' : 'Smart Entry — Natural Text'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {isRTL
                            ? 'اكتب جملة طبيعية أو اضغط 🎤 وتكلّم بصوتك. النظام يحلّل ويملأ الحقول. لن يُحفظ السند إلا بعد مراجعتك وضغط Save.'
                            : 'Type natural text or press 🎤 and speak. The system parses and fills the form. Nothing is saved until you review and click Save.'}
                    </Alert>

                    <Stack direction="row" spacing={1} mb={1.5} alignItems="center" flexWrap="wrap">
                        {voiceListening ? (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<MicOffIcon />}
                                onClick={stopVoiceCapture}
                                sx={{ textTransform: 'none' }}
                            >
                                {isRTL ? '⏹ إيقاف الاستماع' : '⏹ Stop Listening'}
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                startIcon={<MicIcon />}
                                disabled={smartLoading || !isSpeechSupported()}
                                onClick={startVoiceCapture}
                                sx={{ textTransform: 'none' }}
                            >
                                {isRTL ? '🎤 إدخال صوتي' : '🎤 Voice Input'}
                            </Button>
                        )}
                        {/* Language toggle for voice recognition */}
                        {!voiceListening && isSpeechSupported() && (
                            <Stack direction="row" spacing={0.5}>
                                <Chip
                                    size="small"
                                    label="عربي"
                                    color={voiceLang === 'ar-SA' ? 'primary' : 'default'}
                                    onClick={() => setVoiceLang('ar-SA')}
                                    sx={{ cursor: 'pointer' }}
                                />
                                <Chip
                                    size="small"
                                    label="EN"
                                    color={voiceLang === 'en-US' ? 'primary' : 'default'}
                                    onClick={() => setVoiceLang('en-US')}
                                    sx={{ cursor: 'pointer' }}
                                />
                            </Stack>
                        )}
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => { setSmartText(''); voiceRawRef.current = ''; }}
                            sx={{ textTransform: 'none' }}
                        >
                            {isRTL ? '🗑 مسح النص' : '🗑 Clear'}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={async () => {
                                if (!smartText) {
                                    toast.info(isRTL ? 'لا يوجد نص للنسخ' : 'No text to copy');
                                    return;
                                }
                                try {
                                    await navigator.clipboard.writeText(smartText);
                                    toast.success(isRTL ? 'تم نسخ النص بالكامل' : 'Text copied to clipboard');
                                } catch {
                                    // Fallback for older browsers
                                    const ta = document.createElement('textarea');
                                    ta.value = smartText;
                                    document.body.appendChild(ta);
                                    ta.select();
                                    try { document.execCommand('copy'); toast.success(isRTL ? 'تم النسخ' : 'Copied'); }
                                    catch { toast.error(isRTL ? 'فشل النسخ' : 'Copy failed'); }
                                    document.body.removeChild(ta);
                                }
                            }}
                            sx={{ textTransform: 'none' }}
                        >
                            {isRTL ? '📋 نسخ النص' : '📋 Copy'}
                        </Button>
                        {!isSpeechSupported() && (
                            <Typography variant="caption" color="text.secondary">
                                {isRTL
                                    ? '(الإدخال الصوتي يتطلب Chrome أو Edge)'
                                    : '(Voice input requires Chrome or Edge)'}
                            </Typography>
                        )}
                    </Stack>

                    {voiceListening && (
                        <Box sx={{
                            mb: 1.5,
                            p: 2,
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: audioLevel > 10 ? 'success.main' : 'warning.main',
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                        }}>
                            {/* Audio level meter — visual proof the mic is being picked up */}
                            <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                                <MicIcon color={audioLevel > 10 ? 'success' : 'warning'} />
                                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>
                                    {audioLevel > 10
                                        ? (isRTL ? 'الميكروفون يعمل' : 'Mic working')
                                        : (isRTL ? 'لا يُسمع صوت' : 'No audio')}
                                </Typography>
                                {/* Animated bar */}
                                <Box sx={{
                                    flex: 1,
                                    height: 18,
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}>
                                    <Box sx={{
                                        height: '100%',
                                        width: `${audioLevel}%`,
                                        background: audioLevel > 50
                                            ? 'linear-gradient(90deg, #4caf50, #ff9800)'
                                            : audioLevel > 10
                                                ? 'linear-gradient(90deg, #4caf50, #8bc34a)'
                                                : '#9e9e9e',
                                        transition: 'width 0.08s linear',
                                    }} />
                                </Box>
                                <Typography variant="caption" sx={{ minWidth: 32, textAlign: 'end' }}>
                                    {audioLevel}
                                </Typography>
                            </Stack>

                            {/* Live transcript — LARGE font, easy to read on mobile */}
                            <Box sx={{
                                minHeight: 60,
                                p: 1.5,
                                bgcolor: isDarkMode ? 'rgba(0,0,0,0.3)' : 'white',
                                borderRadius: 1,
                                border: '1px dashed',
                                borderColor: 'divider',
                            }}>
                                <Typography sx={{
                                    fontSize: { xs: '1.4rem', sm: '1.5rem' },
                                    fontWeight: 500,
                                    color: voiceInterim ? 'primary.main' : 'text.disabled',
                                    lineHeight: 1.5,
                                    direction: voiceLang === 'ar-SA' ? 'rtl' : 'ltr',
                                    textAlign: voiceLang === 'ar-SA' ? 'right' : 'left',
                                }}>
                                    {voiceInterim || (isRTL ? '🎙 تكلّم بصوت واضح...' : '🎙 Speak clearly...')}
                                </Typography>
                            </Box>

                            {audioLevel < 5 && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'warning.main' }}>
                                    {isRTL
                                        ? '⚠ إذا لم يتحرك المؤشر بعد 3 ثوانٍ من الكلام، الميكروفون معطّل في إعدادات Windows.'
                                        : '⚠ If the meter stays flat after 3s of speaking, mic is disabled in Windows settings.'}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={12}
                        autoFocus
                        value={smartText}
                        onChange={e => { setSmartText(e.target.value); voiceRawRef.current = e.target.value; }}
                        placeholder={isRTL
                            ? 'اكتب هنا أو تكلّم بصوتك...'
                            : 'Type or speak...'}
                        helperText={isRTL
                            ? 'مثال صوتي: "المستفيد شركة العربي، سطر جديد، صيانة الأفنيوز، سطر جديد، 475، سطر جديد، في 6322، سطر جديد، التاريخ 3 3 2026"'
                            : 'Voice example: "Paid to Al-Arabi Company, new line, Avenues maintenance, new line, 475, new line, voucher 6322, new line, date 3 3 2026"'}
                    />
                    <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                        {[
                            'مخصص اجازات المنقف 184.600 Sayed Abdul Sattar 1-2-2026 V 6283',
                            'عهدة سعد 250 لعبدالمعين',
                            'camp maintenance 90 robin',
                            'صرف 25.9 كمبيوتر ، 33.5 قرطاسية ، 32.6 ضيافة',
                        ].map((s, i) => (
                            <Chip key={i} size="small" label={s} onClick={() => setSmartText(s)} sx={{ cursor: 'pointer' }} />
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setSmartOpen(false)} disabled={smartLoading}>
                        {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={applySmartParse}
                        disabled={smartLoading}
                        startIcon={smartLoading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                        sx={{ background: 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)' }}
                    >
                        {isRTL ? 'تحليل وملء السند' : 'Parse & Fill'}
                    </Button>
                </DialogActions>
            </Dialog>

            <DialogContent sx={{ mt: 3, overflowY: 'auto' }}>
                <Stack spacing={3}>
                    {error && (
                        <Alert
                            severity="error"
                            onClose={() => setError('')}
                            sx={{ borderRadius: 2 }}
                        >
                            {error}
                        </Alert>
                    )}

                    <Card
                        elevation={3}
                        sx={{
                            borderRadius: 2,
                            bgcolor: isDarkMode
                                ? 'rgba(255,255,255,0.05)'
                                : 'white',
                            border: isDarkMode
                                ? '1px solid rgba(255,255,255,0.1)'
                                : 'none',
                        }}
                    >
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    color: isDarkMode
                                        ? '#90caf9'
                                        : 'primary.main',
                                    fontWeight: 'bold',
                                }}
                            >
                                {isRTL
                                    ? 'معلومات السند'
                                    : 'Voucher Information'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                <TextField
                                    label={
                                        isRTL ? 'رقم السند' : 'Voucher Number'
                                    }
                                    value={voucherNumber}
                                    onChange={(e) =>
                                        setVoucherNumber(e.target.value)
                                    }
                                    fullWidth
                                    required
                                    placeholder="CP-2026-0001"
                                    InputProps={{
                                        endAdornment: !editVoucher && (
                                            <IconButton
                                                size="small"
                                                onClick={async () =>
                                                    setVoucherNumber(
                                                        await generateAutoNumber()
                                                    )
                                                }
                                                title={
                                                    isRTL
                                                        ? 'إعادة توليد الرقم'
                                                        : 'Regenerate Number'
                                                }
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        ),
                                    }}
                                    sx={{
                                        bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'white',
                                        '& .MuiInputLabel-root': {
                                            color: isDarkMode
                                                ? 'rgba(255,255,255,0.7)'
                                                : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            color: isDarkMode
                                                ? 'white'
                                                : 'inherit',
                                            '& fieldset': {
                                                borderColor: isDarkMode
                                                    ? 'rgba(255,255,255,0.23)'
                                                    : 'inherit',
                                            },
                                        },
                                    }}
                                />
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label={isRTL ? 'التاريخ' : 'Date'}
                                        value={voucherDate ? new Date(voucherDate) : null}
                                        onChange={(date) => setVoucherDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                                        format="dd/MM/yyyy"
                                        views={['year', 'month', 'day']}
                                        openTo="day"
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        slotProps={{ textField: { fullWidth: true, required: true, sx: { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'white', '& .MuiInputLabel-root': { color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'inherit' }, '& .MuiOutlinedInput-root': { color: isDarkMode ? 'white' : 'inherit', '& fieldset': { borderColor: isDarkMode ? 'rgba(255,255,255,0.23)' : 'inherit' } } } } }}
                                    />
                                </LocalizationProvider>
                            </Box>
                            <TextField
                                label={isRTL ? 'اسم المستفيد' : 'Payee Name'}
                                value={payeeName}
                                onChange={(e) => setPayeeName(e.target.value)}
                                fullWidth
                                required
                                placeholder={
                                    isRTL
                                        ? 'أدخل اسم المستفيد'
                                        : 'Enter payee name'
                                }
                                InputProps={{
                                    startAdornment: (
                                        <PersonIcon
                                            sx={{
                                                mr: 1,
                                                color: isDarkMode
                                                    ? 'rgba(255,255,255,0.5)'
                                                    : 'action.active',
                                            }}
                                        />
                                    ),
                                }}
                                sx={{
                                    mt: 2,
                                    bgcolor: isDarkMode
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'white',
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode
                                            ? 'rgba(255,255,255,0.7)'
                                            : 'inherit',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        color: isDarkMode ? 'white' : 'inherit',
                                        '& fieldset': {
                                            borderColor: isDarkMode
                                                ? 'rgba(255,255,255,0.23)'
                                                : 'inherit',
                                        },
                                    },
                                }}
                            />
                            <TextField
                                label={isRTL ? 'ملاحظات' : 'Notes'}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{
                                    mt: 2,
                                    bgcolor: isDarkMode
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'white',
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode
                                            ? 'rgba(255,255,255,0.7)'
                                            : 'inherit',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        color: isDarkMode ? 'white' : 'inherit',
                                        '& fieldset': {
                                            borderColor: isDarkMode
                                                ? 'rgba(255,255,255,0.23)'
                                                : 'inherit',
                                        },
                                    },
                                }}
                            />
                        </CardContent>
                    </Card>

                    <Card
                        elevation={4}
                        sx={{
                            borderRadius: 2,
                            background: isDarkMode
                                ? 'linear-gradient(135deg, rgba(255,152,0,0.15) 0%, rgba(255,193,7,0.15) 100%)'
                                : 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                            border: '2px solid',
                            borderColor: isDarkMode
                                ? 'rgba(255,152,0,0.5)'
                                : 'warning.main',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <AccountBalanceWalletIcon
                                    sx={{
                                        color: isDarkMode
                                            ? '#ffb74d'
                                            : 'warning.dark',
                                        fontSize: 28,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color: isDarkMode
                                            ? '#ffb74d'
                                            : 'warning.dark',
                                    }}
                                >
                                    {isRTL
                                        ? '📤 الطرف الدائن (من الصندوق)'
                                        : '📤 Credit Side (From Cash)'}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <AccountSelector
                                        value={creditAccountId}
                                        onChange={setCreditAccountId}
                                        label={
                                            isRTL ? 'الصندوق' : 'Cash Account'
                                        }
                                        required
                                        parentAccountId={cashParentAccountId}
                                        onlyFinalAccounts={true}
                                    />
                                </Box>
                                <TextField
                                    label={isRTL ? 'المبلغ' : 'Amount'}
                                    type="number"
                                    value={creditAmount}
                                    disabled
                                    sx={{
                                        width: 200,
                                        '& .MuiInputBase-root': {
                                            bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.1)'
                                                : 'rgba(255,255,255,0.9)',
                                            color: isDarkMode
                                                ? '#ffb74d'
                                                : 'inherit',
                                            fontWeight: 'bold',
                                            fontSize: '1.1rem',
                                        },
                                    }}
                                    InputProps={{
                                        inputProps: { min: 0, step: 0.001 },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    <Card
                        elevation={4}
                        sx={{
                            borderRadius: 2,
                            background: isDarkMode
                                ? 'linear-gradient(135deg, rgba(76,175,80,0.15) 0%, rgba(129,199,132,0.15) 100%)'
                                : 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                            border: '2px solid',
                            borderColor: isDarkMode
                                ? 'rgba(76,175,80,0.5)'
                                : 'success.main',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <TrendingUpIcon
                                        sx={{
                                            color: isDarkMode
                                                ? '#81c784'
                                                : 'success.dark',
                                            fontSize: 28,
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{
                                            color: isDarkMode
                                                ? '#81c784'
                                                : 'success.dark',
                                        }}
                                    >
                                        {isRTL
                                            ? '📥 الطرف المدين (إلى)'
                                            : '📥 Debit Side (To)'}
                                    </Typography>
                                </Box>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addDebitLine}
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        background: isDarkMode
                                            ? 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)'
                                            : 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                        boxShadow:
                                            '0 3px 5px 2px rgba(33, 203, 243, .3)',
                                    }}
                                >
                                    {isRTL ? 'إضافة سطر' : 'Add Line'}
                                </Button>
                            </Box>
                            <Box ref={gridRef}>
                            <Stack spacing={2}>
                                {debitLines.map((line, index) => (
                                    <Paper
                                        key={line.id}
                                        elevation={2}
                                        sx={{
                                            p: 2,
                                            bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'rgba(255,255,255,0.9)',
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: isDarkMode
                                                ? 'rgba(255,255,255,0.1)'
                                                : 'divider',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Chip
                                                label={index + 1}
                                                color="primary"
                                                sx={{
                                                    minWidth: 40,
                                                    fontWeight: 'bold',
                                                    bgcolor: isDarkMode
                                                        ? 'rgba(33,150,243,0.3)'
                                                        : 'primary.main',
                                                    color: isDarkMode
                                                        ? '#90caf9'
                                                        : 'white',
                                                }}
                                            />
                                            <Box sx={{ flex: 2 }}>
                                                <AccountSelector
                                                    value={line.accountId}
                                                    onChange={(value) => {
                                                        if (
                                                            value &&
                                                            isAccountUsed(
                                                                value,
                                                                line.id
                                                            )
                                                        ) {
                                                            alert(
                                                                isRTL
                                                                    ? 'هذا الحساب مستخدم بالفعل!'
                                                                    : 'This account is already used!'
                                                            );
                                                            return;
                                                        }
                                                        updateDebitLine(
                                                            line.id,
                                                            'accountId',
                                                            value
                                                        );
                                                    }}
                                                    label={
                                                        isRTL
                                                            ? 'الحساب'
                                                            : 'Account'
                                                    }
                                                    required
                                                    onlyFinalAccounts={true}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <CostCenterSelector
                                                    value={line.costCenterId}
                                                    onChange={(value) =>
                                                        updateDebitLine(
                                                            line.id,
                                                            'costCenterId',
                                                            value
                                                        )
                                                    }
                                                    label={
                                                        isRTL
                                                            ? 'مركز التكلفة'
                                                            : 'Cost Center'
                                                    }
                                                />
                                            </Box>
                                            <TextField
                                                label={
                                                    isRTL ? 'المبلغ' : 'Amount'
                                                }
                                                type="number"
                                                value={line.amount || ''}
                                                onChange={(e) =>
                                                    updateDebitLine(
                                                        line.id,
                                                        'amount',
                                                        parseFloat(
                                                            e.target.value
                                                        ) || 0
                                                    )
                                                }
                                                required
                                                sx={{
                                                    width: 150,
                                                    '& .MuiInputLabel-root': {
                                                        color: isDarkMode
                                                            ? 'rgba(255,255,255,0.7)'
                                                            : 'inherit',
                                                    },
                                                    '& .MuiOutlinedInput-root':
                                                        {
                                                            color: isDarkMode
                                                                ? 'white'
                                                                : 'inherit',
                                                        },
                                                }}
                                                InputProps={{
                                                    inputProps: {
                                                        min: 0,
                                                        step: 0.001,
                                                    },
                                                }}
                                            />
                                            <TextField
                                                label={
                                                    isRTL
                                                        ? 'الوصف'
                                                        : 'Description'
                                                }
                                                value={line.description}
                                                onChange={(e) =>
                                                    updateDebitLine(
                                                        line.id,
                                                        'description',
                                                        e.target.value
                                                    )
                                                }
                                                sx={{
                                                    flex: 1,
                                                    '& .MuiInputLabel-root': {
                                                        color: isDarkMode
                                                            ? 'rgba(255,255,255,0.7)'
                                                            : 'inherit',
                                                    },
                                                    '& .MuiOutlinedInput-root':
                                                        {
                                                            color: isDarkMode
                                                                ? 'white'
                                                                : 'inherit',
                                                        },
                                                }}
                                            />
                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    removeDebitLine(line.id)
                                                }
                                                disabled={
                                                    debitLines.length === 1
                                                }
                                                sx={{ mt: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card
                        elevation={5}
                        sx={{
                            borderRadius: 2,
                            background: isBalanced
                                ? isDarkMode
                                    ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                : isDarkMode
                                  ? 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)'
                                  : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                            >
                                <Typography variant="h5" fontWeight="bold">
                                    {isRTL ? '⚖️ الإجمالي' : '⚖️ Total'}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 3,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.9 }}
                                        >
                                            {isRTL ? 'المدين' : 'Debit'}
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                        >
                                            {totalDebit.toFixed(3)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" fontWeight="bold">
                                        =
                                    </Typography>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.9 }}
                                        >
                                            {isRTL ? 'الدائن' : 'Credit'}
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                        >
                                            {totalCredit.toFixed(3)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h2" fontWeight="bold">
                                        {isBalanced ? '✅' : '❌'}
                                    </Typography>
                                </Box>
                            </Box>
                            {!isBalanced && Math.abs(difference) > 0.01 && (
                                <Alert
                                    severity="warning"
                                    sx={{
                                        mt: 2,
                                        bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.15)'
                                            : 'rgba(255,255,255,0.9)',
                                        color: isDarkMode ? 'white' : 'inherit',
                                    }}
                                >
                                    {isRTL ? 'الفرق: ' : 'Difference: '}
                                    <strong>
                                        {Math.abs(difference).toFixed(3)}
                                    </strong>
                                    {difference > 0
                                        ? isRTL
                                            ? ' (المدين أكبر)'
                                            : ' (Debit is higher)'
                                        : isRTL
                                          ? ' (الدائن أكبر)'
                                          : ' (Credit is higher)'}
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 3,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.100',
                    borderTop: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    size="large"
                    sx={{
                        minWidth: 120,
                        color: isDarkMode ? 'white' : 'inherit',
                    }}
                >
                    {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || !isBalanced || checkingNumber}
                    size="large"
                    startIcon={loading || checkingNumber ? null : <SaveIcon />}
                    sx={{
                        minWidth: 120,
                        background: isDarkMode
                            ? 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)'
                            : 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                        boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
                    }}
                >
                    {loading || checkingNumber
                        ? isRTL
                            ? 'جارٍ المعالجة...'
                            : 'Processing...'
                        : isRTL
                          ? 'حفظ'
                          : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CashPaymentDialog;
